// src/app/components/sso-settings-card.tsx
//
// Admin-only card to enable/disable and configure SSO (Entra ID / Okta / any
// OIDC IdP) at runtime. Backed by GET/PUT /auth/admin/sso. Mounted under
// Global Settings → Platform → Single sign-on, beside the per-customer
// connections (sso-connections-card.tsx).
//
// Local username/password login is never affected by these settings, so an
// admin can always get back in even if SSO is misconfigured.

import { useEffect, useState } from "react";
import { authApi, type SsoAdminConfig, type SsoConfigUpdate } from "@/api/auth";
import {
  Banner, Button, FieldRow, FormStack, SettingsPanel, SkeletonCard, TextInput,
  Toggle,
} from "./kit";

export function SsoSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");   // write-only; blank = unchanged
  const [secretSet, setSecretSet] = useState(false);
  const [redirectUri, setRedirectUri] = useState("");
  const [scopes, setScopes] = useState("");
  const [adminGroup, setAdminGroup] = useState("");

  useEffect(() => {
    let alive = true;
    authApi.getSsoAdminConfig()
      .then((cfg: SsoAdminConfig) => {
        if (!alive) return;
        setEnabled(cfg.enabled);
        setIssuer(cfg.issuer || "");
        setClientId(cfg.client_id || "");
        setSecretSet(cfg.client_secret_set);
        setRedirectUri(cfg.redirect_uri || "");
        setScopes(cfg.scopes || "openid profile email");
        setAdminGroup(cfg.admin_group || "");
      })
      .catch((e) => alive && setError(e?.message || "Failed to load SSO settings"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload: SsoConfigUpdate = {
        enabled,
        issuer: issuer.trim(),
        client_id: clientId.trim(),
        redirect_uri: redirectUri.trim(),
        scopes: scopes.trim(),
        admin_group: adminGroup.trim(),
      };
      // Only send the secret if the admin actually typed one (blank = keep current).
      if (clientSecret) payload.client_secret = clientSecret;

      const cfg = await authApi.updateSsoAdminConfig(payload);
      setEnabled(cfg.enabled);
      setSecretSet(cfg.client_secret_set);
      setClientSecret("");
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save SSO settings");
    } finally {
      setSaving(false);
    }
  };

  // `FieldRow` owns the id, so the label is actually FOR the field. The
  // previous `<Label>` had no `htmlFor` and the `<Input>` had no `id`, so
  // clicking any of the six labels focused nothing and a screen reader read
  // six unnamed inputs on the SSO configuration form.
  const field = (
    label: string, value: string, onChange: (v: string) => void,
    placeholder = "", type = "text",
  ) => (
    <FieldRow label={label}>
      {({ id }) => (
        <TextInput
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          state={saving ? "disabled" : undefined}
        />
      )}
    </FieldRow>
  );

  if (loading) {
    return <SkeletonCard />;
  }

  return (
    <SettingsPanel title="Single Sign-On (SSO)" className="upm-sso">
      <p className="upm-settings-panel-column__lede">
        Let users sign in with your identity provider (Microsoft Entra ID, Okta, or any
        OIDC IdP). Username and password login keeps working regardless of this setting.
      </p>

      {error ? <Banner tone="error">{error}</Banner> : null}
      {saved ? <Banner tone="ok">SSO settings saved.</Banner> : null}

      <div className="upm-admin__choice">
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label="Enable SSO"
          state={saving ? "disabled" : undefined}
        />
        <p className="upm-admin__quiet">
          Shows the "Sign in with SSO" button and accepts IdP logins.
        </p>
      </div>

      <FormStack>

      {field("Issuer URL", issuer, setIssuer,
        "https://login.microsoftonline.com/<tenant-id>/v2.0")}
      {field("Client ID", clientId, setClientId, "Application (client) ID")}
      {field(
        secretSet ? "Client secret, set. Leave blank to keep" : "Client secret (optional for SPA)",
        clientSecret, setClientSecret,
        secretSet ? "••••••••" : "Only for confidential/web apps", "password",
      )}
      {field("Redirect URI", redirectUri, setRedirectUri,
        `${window.location.origin}/`)}
      {field("Scopes", scopes, setScopes, "openid profile email")}
      {field("Admin group (optional)", adminGroup, setAdminGroup,
        "Group claim that grants admin, leave blank for none")}

        <div className="upm-row upm-row--end">
          <Button
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            loadingLabel="Saving"
          >
            Save SSO settings
          </Button>
        </div>
      </FormStack>
    </SettingsPanel>
  );
}
